/**
 * SearchFilterChip — the removable "you are looking at one search's imports"
 * affordance for `/research/imports?search_id_thrive=<id>` (epic sub-task 03).
 * Research ▸ Company Lists reuses it for the same URL-held filter.
 *
 * Unlike the owner / tag / text filters, this one lives in the URL rather than
 * in component state: the My Searches row CTA deep-links into it, so it has to
 * survive a paste, a refresh and a shared link. That makes the chip the only
 * thing on screen telling the recruiter why the list is short — hence a visible
 * chip rather than a silent filter, and a clear button rather than only the
 * browser's back navigation.
 *
 * Design-system token utilities only, English copy only.
 */
import type { ImportSummary } from "@nu-assist/contracts";
import { X } from "lucide-react";

export interface ResolveSearchFilterLabelArgs {
  /** The Thrive search id the list is filtered by. */
  searchId: string;
  /** Optional display name carried by the CTA (`?search_name=`). */
  nameFromUrl?: string | null;
  /**
   * The current page's rows — they carry the cached search name. Typed by the
   * two fields read, so Company Lists (whose rows carry the same pair) shares
   * the label rule instead of growing a second one.
   */
  items: ReadonlyArray<Pick<ImportSummary, "search_id_thrive" | "search_name">>;
}

/**
 * Best available human label for the filtered search — ALWAYS id-prefixed.
 *
 * The id leads (`5001 · Acme Corp - Chief Financial Officer`) because the id is
 * what the recruiter matched on: every row in the list beneath shows
 * `<id> · <name>`, the search typeahead is searched BY id, and Thrive itself is
 * addressed by id. A chip carrying only the name made the reader confirm by
 * eye that the name above matched the names below, when the id they typed was
 * the thing they wanted echoed back.
 *
 * The URL-carried name wins so the label is stable from the first paint and
 * does not change once the request resolves. It falls back to the cached name on
 * a returned row (deep-links pasted without a name), and finally to the id
 * alone — the honest answer when the search has NO imports at all, the case the
 * chip most needs to explain, and the reason the id is not simply concatenated
 * onto a name that might not exist.
 */
export function resolveSearchFilterLabel({
  searchId,
  nameFromUrl,
  items,
}: ResolveSearchFilterLabelArgs): string {
  const fromUrl = nameFromUrl?.trim();
  if (fromUrl) return `${searchId} · ${fromUrl}`;
  const fromRow = items.find((it) => it.search_id_thrive === searchId)?.search_name;
  if (fromRow?.trim()) return `${searchId} · ${fromRow.trim()}`;
  return `Search ${searchId}`;
}

export interface SearchFilterChipProps {
  label: string;
  onClear: () => void;
}

export function SearchFilterChip({ label, onClear }: SearchFilterChipProps) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="imports-search-filter">
      <span className="text-label text-text-secondary">Filtered by search:</span>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-accent bg-accent-muted px-3 py-1 text-label text-accent">
        <span data-testid="imports-search-filter-label">{label}</span>
        <button
          type="button"
          onClick={onClear}
          aria-label={`Clear the ${label} search filter`}
          data-testid="imports-search-filter-clear"
          className="rounded-full p-0.5 hover:bg-surface-raised focus:outline-none focus:ring-1 focus:ring-accent"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </span>
    </div>
  );
}
